import * as _ from "lodash";
import * as React from "react";
import styled, { css } from "styled-components";

import Grid from "src/library/components/Hedron";
import { ActionsPopOut } from "src/library/components/ActionsPopOut/index";
import { Input } from "src/library/components/Forms";
import { Icon } from "src/library/components/Icon";
import { Text } from "src/library/components/Text";
import { Icons } from "src/library/components/Icon/types";
import {
  ITag,
  AvailableProductTagIcons
} from "src/modules/product-tags/api/product-tags.api";

import ProductTag from "./ProductTag";

interface ProductTagIconSelectorProps {
  onSelect: (type: "iconCode" | "icon", data: { iconCode?: string | null; icon?: string | null }) => void;
  productTag: ITag;
}

/**
 *
 * Component to handle order additions
 */
const ProductTagIconSelector: React.ReactFCWithChildren<ProductTagIconSelectorProps> = ({ productTag, onSelect }) => {
  const [ iconOrCode, setIconOrCode ] = React.useState<"icon" | "iconCode">(productTag.iconCode ? "iconCode" : "icon");
  const [ iconCode, setIconCode ] = React.useState<string | null | undefined>(productTag.iconCode);
  const [ icon, setIcon ] = React.useState<AvailableProductTagIcons | null | undefined>(productTag.icon);
  const [ showIconSelector, setShowIconSelector ] = React.useState(false);
  const [ error, setError ] = React.useState<string | null>(null);

  const allowedIcons: AvailableProductTagIcons[] = [
    Icons.EGG,
    Icons.FISH,
    Icons.SHELLFISH,
    Icons.NUTS,
    Icons.ADVISORY,
    Icons.DAIRY,
    Icons.WHEAT,
    Icons.VEGAN,
    Icons.OVEN,
    Icons.FROZEN,
    Icons.PAN,
    Icons.HALAL,
    Icons.SESAME,
    Icons.MUSTARD,
    Icons.LUPIN,
    Icons.PEANUTS,
    Icons.SULPHITES,
    Icons.PORK,
    Icons.MOLLUSCS,
    Icons.SUGAR,
    Icons.CORN,
    Icons.TRANSFATS,
    Icons.SOY,
    Icons.CELERY,
    Icons.ALCOHOL,
    Icons.MEAT,
    Icons.SHELLFISH1,
    Icons.VEGAN1
  ];

  const handleSelect = React.useCallback(() => {
    onSelect(iconOrCode, {
      icon,
      iconCode
    });
  }, [
    iconOrCode,
    iconCode,
    icon
  ]);

  // validating for confirm
  const canSave = React.useMemo(() => {
    if (iconOrCode === "iconCode") {
      if (!iconCode || !iconCode.length) {
        return false;
      }
    }

    if (iconOrCode === "icon") {
      if (!icon) {
        return false;
      }
    }

    return true;
  }, [
    iconCode,
    icon,
    iconOrCode
  ]);

  return (
    <ProductTagIconSelectorWrapper>
      <Grid.Bounds
        className="select-icon"
        onClick={() => setShowIconSelector(!showIconSelector)}
      >
        <ProductTag {...productTag} />

        <Icon
          icon={Icons.CHEVRONDOWN}
          $fillColor="lightGrey"
        />
      </Grid.Bounds>

      {showIconSelector && (
        <ActionsPopOut
          action={() => handleSelect()}
          directionHorizontal="left"
          directionVertical="top"
          handleClose={() => setShowIconSelector(false)}
          steps={[
            {
              padComponent: false,
              component: (
                <>
                  <IconOrCode>
                    <Grid.Box
                      className={`icon-code-tab ${iconOrCode === "icon" && "active"}`}
                      onClick={() => {
                        setError(null);
                        setIconOrCode("icon");
                      }}
                    >
                      <Text
                        weight="bold"
                        className="icon-code-text"
                      >
                        Icon
                      </Text>
                    </Grid.Box>

                    <Grid.Box
                      className={`icon-code-tab ${iconOrCode === "iconCode" && "active"}`}
                      onClick={() => {
                        setError(null);
                        setIconOrCode("iconCode");
                      }}
                    >
                      <Text
                        weight="bold"
                        className="icon-code-text"
                      >
                        Code
                      </Text>
                    </Grid.Box>
                  </IconOrCode>

                  <SelectorInner>
                    {iconOrCode === "iconCode" && (
                      <Input
                        value={iconCode || ""}
                        handleChange={e => {
                          if (e.target.value.length <= 2) {
                            setError(null);
                            setIconCode((e.target.value as string).toUpperCase());
                          }

                          if (e.target.value.length === 0) {
                            setError("Code must be at least one character!");
                          }
                        }}
                        error={error || ""}
                        label="Code (max 2 letters)"
                        name="iconCode"
                        as="input"
                      />
                    )}

                    {iconOrCode === "icon" && (
                      <IconList>
                        {_.map(allowedIcons, allowedIcon => (
                          <Grid.Box
                            onClick={() => {
                              setIcon(allowedIcon);
                            }}
                            className={`icon-list-icon ${allowedIcon === icon && "active"}`}
                          >
                            <ProductTag
                              {...productTag}
                              icon={allowedIcon}
                            />
                          </Grid.Box>
                        ))}
                      </IconList>
                    )}
                  </SelectorInner>
                </>
              ),
              allowNext: canSave
            }
          ]}
        />
      )}
    </ProductTagIconSelectorWrapper>
  );
};

const IconList = styled(Grid.Bounds)`
  ${({ theme }) => css`
    flex-wrap: wrap;

    .icon-list-icon {
      margin: ${theme.spacing.margins.sm};
      margin-bottom: ${theme.spacing.margins.sm};

      .icon-wrapper {
        background: ${theme.colors.midGrey}!important;

        &:hover {
          background: ${theme.colors.primaryLight}!important;
        }
      }

      &.active {
        .icon-wrapper {
          background: ${theme.colors.primaryDark}!important;
        }
      }
    }
  `}
`;

const SelectorInner = styled(Grid.Bounds)`
  ${({ theme }) => css`
    padding: ${theme.spacing.padding.md};
  `}
`;

const IconOrCode = styled(Grid.Bounds)`
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.colors.lightGrey};
    .icon-code-tab {
      width: 50%;
      padding: 10px;

      &:hover {
        cursor: pointer;
      }

      &.active {
        background: ${theme.colors.primaryDark};
        .icon-code-text {
          color: ${theme.colors.white};
        }
      }

      &:first-child {
        border-right: 1px solid ${theme.colors.lightGrey};
      }
    }
  `}
`;

const ProductTagIconSelectorWrapper = styled.div`
  ${() => css`
    position: relative;

    .select-icon {
      align-items: center;
      cursor: pointer;
    }
  `}
`;

export default ProductTagIconSelector;
